import { memo, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ResizeableSidebar from "../../../components/sidebar/ResizeableSidebar";
import { sidebarClear } from "../../../redux/actions/sidebarActions";
import {
  PANEL_DEFAULT_WIDTH,
  panelWidth,
} from "../../../components/sidebar/shared/panelChrome";

/**
 * DrilldownSidebarPane — now a thin wrapper over ResizeableSidebar so the
 * drilldown "add / edit" panes share the look and feel of the detail
 * sidebars (LoanBorrowerSidebar, LoanSubjectPropertySidebar,
 * LoanRelatedContactSidebar).
 *
 * Preserves the original prop shape so callers (AddLoanRelatedContact,
 * AddBorrower, AddException, etc.) work unchanged. On close it still
 * dispatches `sidebarClear()` after the caller's `onClose` fires.
 *
 * Behavioral changes from the original implementation:
 *   - Always renders as a fixed overlay on the right (prior `variant="inline"`
 *     used to take inline flex space). Backdrop is hidden by default so it
 *     mirrors the detail sidebars.
 *   - Auto-close-at-<220px drag behavior removed; ResizeableSidebar enforces
 *     a 320px min instead.
 *   - `onCloseIcon` is no longer customizable — always the standard Close X.
 */
const DrilldownSidebarPane = memo(
  ({
    title,
    children,
    icon,
    // Default opening width; anything narrower than PANEL_DEFAULT_WIDTH is
    // raised to it (see numericWidth below).
    initialWidth,
    fixedWidth,
    bodyPadding,
    bodySx,
    sx,
    headerSx,
    // Pinned bottom bar (a form's action row). Sibling of the scrolling body.
    footerContent,
    titleBottomContent,
    onClose,
    hideBackdrop = true,
    closeButtonStartContent,
    hideHeader = false,
    descriptionContent,
    backgroundColorGray,
    displayCollapsedSidebar = false,
    hideDivider,
    // Unlisted props (maxWidth, minWidth, variant, bodyOverflow, onCloseIcon)
    // from the original DrilldownSidebarPane API are accepted by callers but
    // silently ignored by the ResizeableSidebar-backed impl — React only
    // passes destructured props through, so extras are harmlessly dropped.
  }) => {
    const dispatch = useDispatch();

    // Remember the pop-out layout per loan + panel so it survives drilldown tab
    // changes (the pane remounts, but ResizeableSidebar restores from this key).
    // A different loan / panel starts fresh.
    const sidebarType = useSelector((state) => state.sidebar?.type);
    const loanId = useSelector((state) => state.loanDrilldown?._id);
    const persistKey =
      sidebarType && loanId ? `${loanId}:${sidebarType}` : undefined;

    const handleClose = useCallback(() => {
      if (onClose) onClose();
      dispatch(sidebarClear());
    }, [onClose, dispatch]);

    // Opening width. A caller's `initialWidth` is a FLOOR-RAISED hint, not the
    // final word: every pane opens at least PANEL_DEFAULT_WIDTH (the calendar
    // drawer's width) so the drilldown's forms all start roomy and consistent.
    // The per-caller values stay meaningful for the panes that ask for MORE
    // than that (the exception editor, the UW checklist). Clamped to the
    // viewport, and drag-resizable down to 320px from there.
    const numericWidth = useMemo(() => {
      const raw = fixedWidth || initialWidth;
      let requested = PANEL_DEFAULT_WIDTH;
      if (typeof raw === "number") requested = raw;
      else if (typeof raw === "string") {
        if (raw.endsWith("vw")) {
          requested = Math.round((parseInt(raw, 10) / 100) * window.innerWidth);
        } else {
          requested = parseInt(raw, 10) || PANEL_DEFAULT_WIDTH;
        }
      }
      return panelWidth(Math.max(requested, PANEL_DEFAULT_WIDTH));
    }, [fixedWidth, initialWidth]);

    // When `hideHeader` is on, collapse the header bar entirely (matches the
    // `SideCardModal` pattern used by FilePreviewSideCardModal). Callers that
    // opt in must render their own close affordance inside `children`.
    const resolvedHeaderSx = hideHeader
      ? { display: "none", ...headerSx }
      : headerSx;

    const styles = useMemo(() => {
      if (displayCollapsedSidebar) {
        // Flush to the screen's right edge (no gap) and ABOVE the collapsed icon
        // rail (zIndex 111) so the pane covers it — close button sits at the
        // true right edge, no reserved padding.
        return { right: 0, zIndex: 13000, ...sx };
      }
      return { ...sx };
    }, [sx, displayCollapsedSidebar]);

    return (
      <ResizeableSidebar
        open
        hideBackdrop={hideBackdrop}
        hideDivider={hideHeader || hideDivider}
        title={title}
        titleIcon={icon}
        backgroundColorGray={backgroundColorGray}
        onClose={handleClose}
        descriptionContent={descriptionContent}
        closeButtonStartContent={closeButtonStartContent}
        defaultWidth={numericWidth}
        bodyPadding={bodyPadding}
        bodySx={bodySx}
        footerContent={footerContent}
        headerSx={resolvedHeaderSx}
        headerBottomContent={titleBottomContent}
        sx={styles}
        persistKey={persistKey}
      >
        {children}
      </ResizeableSidebar>
    );
  },
);

DrilldownSidebarPane.displayName = "DrilldownSidebarPane";

export default DrilldownSidebarPane;
